import { cva } from 'class-variance-authority';
import { Button, Flex, Icon, Text } from '@nula/core-ui';
import { surfaceMeta } from '@/lib/sync/surface-meta';
import styles from './surface-pending-row.module.css';

export interface PendingSurfaceRow {
  runId: string;
  name: string;
  surface: 'text' | 'canvas';
  couldNotStart: boolean;
}

interface SurfacePendingRowProps {
  row: PendingSurfaceRow;
  onRemove: (runId: string) => void;
}

const pendingRow = cva(styles.pendingRow);
const dot = cva(styles.dot, {
  variants: {
    couldNotStart: { true: styles.dotFailed, false: styles.dotStarting },
  },
});

export function SurfacePendingRow({ row, onRemove }: SurfacePendingRowProps) {
  const meta = surfaceMeta(row);
  const state = row.couldNotStart ? "Couldn't start" : 'Starting';
  return (
    <Flex align="center" role="none" className={pendingRow()} data-run-id={row.runId}>
      <Flex
        align="center"
        role="menuitem"
        aria-disabled="true"
        aria-label={`${row.name}, ${state}`}
        tabIndex={-1}
        className={styles.item}
      >
        <Flex component="span" className={styles.rail} />
        <Icon name={meta.icon} className={styles.icon} size={18} />
        <Text className={styles.name}>{row.name}</Text>
        <Flex component="span" align="center" className={styles.state}>
          <Flex
            component="span"
            aria-hidden="true"
            data-testid="pending-state-dot"
            className={dot({ couldNotStart: row.couldNotStart })}
          />
          <Text size="xs" type="secondary">{state}</Text>
        </Flex>
      </Flex>
      {row.couldNotStart && (
        <Button
          size="sm"
          variant="text"
          role="menuitem"
          tabIndex={-1}
          aria-label={`Remove ${row.name}`}
          onClick={() => onRemove(row.runId)}
        >
          Remove
        </Button>
      )}
    </Flex>
  );
}
